import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDom from 'react-dom';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps } from 'types';
import Map from 'components/Map';
import MapSettings from 'components/Map/settings';
import { zoomToFeature } from 'components/Map/helper';
import {
  getFeatureServerUrl,
  featureRequestInterceptors
} from 'services/map.service';
import {
  queryFeatureLayerExtentStart,
  clearFeatureLayerLayerExtent
} from 'redux/map/actions';
import { toLocalDateTimeFormat } from 'utilities/helper.util';
import { getCurrentAOIInfoStart } from 'redux/areaOfInterest';
import { SkeletonText } from 'carbon-components-react';
import { useComponentMount } from 'utilities/hooks.util';
import EmptyData from './empty';

const {
  modules,
  currentAOIDarkSymbol,
  currentAOIlightOutlineSymbol,
  AUS_Extent,
  wkid
} = MapSettings;

export const renderLegend = (
  dom: HTMLDivElement,
  organisationName?: string,
  utilityId?: string,
  aoiCreatedAt?: string,
  aoiArea?: string
) => {
  return ReactDom.render(
    organisationName && utilityId ? (
      <div className="legend-box">
        <div className="legend-box-section">
          <div className="name">{organisationName}</div>
          <div className="utilityId">Utility ID: {utilityId}</div>
        </div>
        <div className="legend-box-section">
          {aoiCreatedAt ? (
            <div className="date">AOI updated: {aoiCreatedAt}</div>
          ) : (
            <SkeletonText />
          )}
          {aoiArea ? (
            <div className="area">Area: {aoiArea} km²</div>
          ) : (
            <SkeletonText />
          )}
        </div>
      </div>
    ) : (
      <></>
    ),
    dom
  );
};

export const AoiViewerMapView: React.FC<Props> = ({
  aoiLayerExtent,
  organisationId,
  organisationName,
  utilityId,
  currentActiveAoi,
  currentInfo,
  resetSearch,
  baseMapTheme
}) => {
  const extraMapModules = [modules.webMercatorUtils, modules.featureLayer];
  const [mapReady, setMapReady] = useState<boolean>(false);
  const [hasNoAOI, setHasNoAOI] = useState<boolean>(false);
  const [aoiCreatedAt, setAoiCreatedAt] = useState<string>('');
  const [aoiArea, setAoiArea] = useState<string>('');
  const legendDiv = useRef<HTMLDivElement>();

  const dispatch = useDispatch();
  const onMapReady = () => {
    const { loadedMapModules, loadedMap, loadedView } = window;
    const { Config, FeatureLayer } = loadedMapModules;
    Config.request.interceptors.push({
      urls: getFeatureServerUrl('AreasOfInterest'),
      before: featureRequestInterceptors
    });

    const currentFeatureLayer = new FeatureLayer({
      id: 'aois-layer',
      url: getFeatureServerUrl('AreasOfInterest'),
      outFields: ['objectid'],
      definitionExpression: 'organisation_id = 0',
      renderer: {
        type: 'simple',
        symbol:
          baseMapTheme === 'dark'
            ? currentAOIDarkSymbol
            : currentAOIlightOutlineSymbol
      }
    });

    loadedMap.add(currentFeatureLayer, 0);

    if (!legendDiv.current) {
      const legendContainer = document.createElement('div');
      legendContainer.className = 'map--legend-widget';
      loadedView.ui.add(legendContainer, 'top-right');
      legendDiv.current = legendContainer;
    }

    setMapReady(true);
  };

  const setDefinitionExpression = useCallback(
    (organisationId: number) => {
      const { loadedMap } = window;

      if (loadedMap) {
        const layer = loadedMap.findLayerById('aois-layer');
        const definitionExpression = `organisation_id = ${organisationId} AND deleted_at IS NULL`;
        if (layer) {
          layer.definitionExpression = definitionExpression;
          dispatch(queryFeatureLayerExtentStart(layer));
        }
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (aoiLayerExtent) {
      if (typeof aoiLayerExtent !== 'string') {
        if (!aoiLayerExtent.noRecord) {
          zoomToFeature(aoiLayerExtent);
          window.mapOriginalExtent = aoiLayerExtent;
        } else {
          const { loadedMapModules } = window;
          if (loadedMapModules) {
            const { Extent, SpatialReference } = loadedMapModules;
            const ausExtent = new Extent({
              ...AUS_Extent,
              spatialReference: new SpatialReference({ wkid })
            });
            window.mapOriginalExtent = ausExtent;
          }
        }
      }
    }
  }, [aoiLayerExtent, dispatch]);

  useEffect(() => {
    organisationId && mapReady && setDefinitionExpression(organisationId);
  }, [organisationId, mapReady, setDefinitionExpression]);

  useEffect(() => {
    if (currentActiveAoi) {
      if (currentActiveAoi === 'empty') {
        setHasNoAOI(true);
      } else {
        setHasNoAOI(false);
        setAoiCreatedAt(toLocalDateTimeFormat(currentActiveAoi.createdAt));
        dispatch(
          getCurrentAOIInfoStart({
            aoiId: currentActiveAoi.id,
            organisationId,
            returnEnquiryVolume: false
          })
        );
      }
    } else {
      setHasNoAOI(false);
      setAoiCreatedAt('');
      setAoiArea('');
    }
  }, [currentActiveAoi, organisationId, dispatch]);

  useEffect(() => {
    if (typeof currentInfo !== 'string') {
      currentInfo?.area && setAoiArea(currentInfo.area);
    }
  }, [currentInfo]);

  useEffect(() => {
    if (legendDiv.current) {
      renderLegend(
        legendDiv.current,
        organisationName,
        utilityId,
        aoiCreatedAt,
        aoiArea
      );
    }
  }, [organisationName, utilityId, aoiCreatedAt, aoiArea, mapReady]);

  useComponentMount(() => {
    return () => {
      dispatch(clearFeatureLayerLayerExtent());
    };
  });

  return (
    <div className="aoi-viewer-map--view">
      {hasNoAOI && organisationName && utilityId && (
        <EmptyData
          organisationName={organisationName}
          utilityId={utilityId}
          resetSearch={resetSearch}
        />
      )}
      <Map
        showbaseMapButton={false}
        showHomeButton={true}
        showCurrentLocationButton={false}
        onReady={onMapReady}
        extraModules={extraMapModules}
      />
    </div>
  );
};

const mapStateToProps = ({ mapCache, aoiCache, app }: RootState) => ({
  aoiLayerExtent: mapCache.featureLayerExtent,
  currentActiveAoi: aoiCache.currentActiveAoi,
  currentInfo: aoiCache.currentInfo,
  baseMapTheme: app.baseMapTheme
});

const connector = connect(mapStateToProps);

type OwnProps = {
  organisationId?: number;
  organisationName?: string;
  utilityId?: string;
  resetSearch: Function;
};

type Props = CFCProps<typeof connector> & OwnProps;

export default React.memo(connector(AoiViewerMapView));
